// src/components/ScheduleModal/index.js
//
// Modal de agendamento no padrão "Aeros Dark" — layout do mockup
// "Tela Aeros Dark - Agendamentos" (seção Modal de agendamento). A lógica de
// dados (contatos, conexões, respostas rápidas, anexo, recorrência, lembrete,
// canal SMS) é a mesma da versão anterior; só a apresentação mudou.

import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import MotivoFalha from "../MotivoFalha";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import InputAdornment from "@material-ui/core/InputAdornment";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import CodeIcon from "@material-ui/icons/Code";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import RepeatIcon from "@material-ui/icons/Repeat";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import EventRepeatIcon from "@material-ui/icons/Update";
import SendIcon from "@material-ui/icons/Send";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import moment from "moment";
import { isArray, head } from "lodash";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";
import useQueues from "../../hooks/useQueues";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { nextSendDates } from "../../pages/Schedules/scheduleRules";
import {
  tokensDoTemplate,
  textoDoBody,
  previa,
  deveLimparTemplateNaTrocaDeConexao,
} from "../CampaignModal/TemplateFields";
import {
  conexaoEhOficial,
  exigeTemplate,
  parseTemplateParams,
  payloadTemplate,
  templateIncompleto,
  avisoTemplate,
} from "./scheduleTemplateRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  const purpleText = stateText(theme, "#C08BFF", "purple");
  const cyanText = stateText(theme, AEROS.cyan, "info");
  return {
    paper: {
      width: 680,
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(var(--vh-full) - 48px)",
      margin: 16,
    },
    header: {
      padding: "20px 24px 16px",
      display: "flex",
      alignItems: "center",
      borderBottom: `1px solid ${border}`,
      flexShrink: 0,
    },
    title: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: theme.palette.text.primary,
    },
    subtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    closeButton: {
      marginLeft: "auto",
      padding: 4,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 20 },
    },
    body: {
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 18,
      overflowY: "auto",
      ...theme.scrollbarStyles,
      // filhos com overflow:hidden (seção Recorrência) encolheriam a 0 no
      // flex-column quando o conteúdo passa da altura do modal
      "& > *": { flexShrink: 0 },
    },
    grid3: {
      display: "grid",
      gridTemplateColumns: "1.4fr 1fr 0.8fr",
      gap: 12,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    grid2: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    fieldLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: 6,
      display: "block",
    },
    input: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: theme.palette.aeros.surface,
        fontSize: 13,
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: borderInput },
      "& .MuiOutlinedInput-input": { padding: "9px 12px" },
      "& .MuiOutlinedInput-adornedStart": { paddingLeft: 12 },
      "& .MuiOutlinedInput-adornedStart .MuiOutlinedInput-input": { paddingLeft: 0 },
      "& .MuiOutlinedInput-multiline": { padding: "12px 14px" },
      "& .MuiOutlinedInput-multiline .MuiOutlinedInput-input": { padding: 0 },
      "& .MuiSelect-root": { padding: "9px 32px 9px 12px" },
      "& .MuiSelect-icon": { color: theme.palette.text.secondary, right: 8 },
      "& .MuiAutocomplete-inputRoot": { padding: "1px 12px" },
      "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": { padding: "8px 0" },
      "& .MuiAutocomplete-endAdornment": { right: 6 },
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    inputSmall: {
      "& .MuiOutlinedInput-root": { borderRadius: 9, fontSize: 12.5 },
      "& .MuiOutlinedInput-input": { padding: "8px 11px" },
      "& .MuiSelect-root": { padding: "8px 30px 8px 11px" },
      "& .MuiSelect-icon": { fontSize: 16 },
    },
    dateIcon: { color: cyanText, fontSize: 16 },
    segmented: {
      display: "flex",
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      overflow: "hidden",
      fontSize: 12,
      fontWeight: 600,
    },
    segment: {
      flex: 1,
      textAlign: "center",
      padding: "9px 0",
      color: theme.palette.text.secondary,
      background: "transparent",
      border: "none",
      cursor: "pointer",
      font: "inherit",
      fontWeight: 600,
      "&:hover": { color: theme.palette.text.primary },
    },
    segmentActive: {
      backgroundColor: "rgba(160,68,255,0.2)",
      color: purpleText,
      "&:hover": { color: purpleText },
    },
    messageHead: {
      display: "flex",
      alignItems: "center",
      marginBottom: 6,
    },
    messageTools: { marginLeft: "auto", display: "flex", gap: 6 },
    toolChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      font: "inherit",
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.5,
      color: theme.palette.text.secondary,
      border: `1px solid ${borderInput}`,
      borderRadius: 6,
      padding: "2px 8px",
      background: "transparent",
      cursor: "pointer",
      "& .MuiSvgIcon-root": { fontSize: 13 },
      "&:hover": { color: theme.palette.text.primary },
      "&:disabled": { opacity: 0.5, cursor: "default" },
    },
    toolChipPurple: {
      color: purpleText,
      backgroundColor: "rgba(160,68,255,0.12)",
      borderColor: "transparent",
      "&:hover": { color: purpleText },
    },
    messageInput: {
      "& .MuiOutlinedInput-root": { fontSize: 13.5, lineHeight: 1.6 },
    },
    dateInput: {
      "& input::-webkit-calendar-picker-indicator": { display: "none" },
      "& .MuiInputAdornment-root": { cursor: "pointer" },
    },
    varRow: { display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" },
    varChip: {
      fontSize: 10.5,
      fontWeight: 600,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      color: theme.palette.text.secondary,
      border: `1px dashed ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.15)"}`,
      borderRadius: 6,
      padding: "2px 9px",
      background: "transparent",
      cursor: "pointer",
      "&:hover": { color: theme.palette.text.primary },
    },
    varChipPrimary: {
      color: cyanText,
      backgroundColor: alpha(AEROS.cyan, 0.08),
      borderColor: alpha(AEROS.cyan, 0.3),
      "&:hover": { color: cyanText },
    },
    quickList: {
      marginTop: 6,
      maxHeight: 180,
      overflowY: "auto",
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      backgroundColor: theme.palette.aeros.surface,
      ...theme.scrollbarStyles,
    },
    quickSearch: {
      padding: "8px 10px",
      borderBottom: `1px solid ${border}`,
    },
    quickItem: {
      padding: "8px 12px",
      display: "flex",
      alignItems: "center",
      gap: 8,
      cursor: "pointer",
      borderBottom: `1px solid ${border}`,
      "&:last-child": { borderBottom: "none" },
      "&:hover": { backgroundColor: "rgba(160,68,255,0.06)" },
      "& .MuiSvgIcon-root": { fontSize: 14, color: purpleText, flex: "none" },
    },
    quickShortcode: { fontSize: 12.5, fontWeight: 600, color: theme.palette.text.primary },
    quickMessage: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    quickEmpty: {
      padding: 12,
      textAlign: "center",
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
    },
    helperText: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      marginTop: 6,
    },
    helperError: { color: stateText(theme, AEROS.error, "error") },
    attachmentRow: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      marginTop: 8,
      fontSize: 12,
      color: theme.palette.text.primary,
      border: `1px solid ${borderInput}`,
      borderRadius: 8,
      padding: "4px 6px 4px 10px",
      maxWidth: "100%",
      "& .MuiSvgIcon-root": { fontSize: 15, color: theme.palette.text.secondary },
    },
    attachmentName: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    attachmentRemove: {
      padding: 2,
      "&:hover .MuiSvgIcon-root": { color: AEROS.error },
    },
    toggleRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "6px 0",
      minHeight: 40,
    },
    toggleText: { fontSize: 12.5, color: theme.palette.text.secondary },
    toggleStrong: { color: theme.palette.text.primary, fontWeight: 700 },
    ticketDetails: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
      marginTop: 4,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    ticketDetailsFull: { gridColumn: "1 / -1" },
    assinarRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
    },
    section: {
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRadius: 12,
      overflow: "hidden",
    },
    sectionHead: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "12px 16px",
      backgroundColor: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)",
      "& > .MuiSvgIcon-root": { fontSize: 18, color: purpleText },
    },
    sectionTitle: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    templateBody: {
      padding: "12px 16px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    templateGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: 10,
    },
    templatePreview: {
      fontSize: 12.5,
      lineHeight: 1.5,
      color: theme.palette.text.secondary,
      backgroundColor: isLight ? "rgba(15,20,40,0.03)" : "rgba(255,255,255,0.03)",
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
      borderRadius: 10,
      padding: "10px 12px",
      whiteSpace: "pre-wrap",
    },
    templatePreviewLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      color: theme.palette.text.secondary,
      marginBottom: 4,
    },
    templateAlert: {
      fontSize: 12,
      lineHeight: 1.45,
      borderRadius: 10,
      padding: "9px 12px",
    },
    templateAlertInfo: {
      color: stateText(theme, AEROS.cyan, "info"),
      backgroundColor: alpha(AEROS.cyan, 0.1),
      border: `1px solid ${alpha(AEROS.cyan, 0.28)}`,
    },
    templateAlertWarning: {
      color: stateText(theme, AEROS.warning, "warning"),
      backgroundColor: alpha(AEROS.warning, 0.1),
      border: `1px solid ${alpha(AEROS.warning, 0.28)}`,
    },
    templateAlertError: {
      color: stateText(theme, AEROS.error, "error"),
      backgroundColor: alpha(AEROS.error, 0.1),
      border: `1px solid ${alpha(AEROS.error, 0.28)}`,
    },
    sectionDesc: { fontSize: 11.5, color: theme.palette.text.secondary },
    recurrenceGrid: {
      display: "grid",
      gridTemplateColumns: "1.25fr 1fr 1.45fr",
      gap: 10,
      padding: "12px 16px",
      borderTop: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    everyCell: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      "& $everyPrefix": { fontSize: 12.5, color: theme.palette.text.secondary, flex: "none" },
    },
    everyPrefix: {},
    everyNumber: { width: 52, flex: "none" },
    everyUnit: { flex: 1, minWidth: 0 },
    reminder: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "11px 16px",
      border: `1px dashed ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.12)"}`,
      borderRadius: 12,
      flexWrap: "wrap",
      "& > .MuiSvgIcon-root": { fontSize: 18, color: AEROS.warning },
    },
    reminderText: {
      flex: 1,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      minWidth: 180,
    },
    reminderLink: {
      fontSize: 12,
      color: cyanText,
      background: "none",
      border: "none",
      cursor: "pointer",
      font: "inherit",
      fontWeight: 600,
      padding: 0,
      "&:hover": { textDecoration: "underline" },
    },
    reminderField: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      width: "100%",
      marginTop: 4,
    },
    footer: {
      padding: "16px 24px",
      borderTop: `1px solid ${border}`,
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexShrink: 0,
      flexWrap: "wrap",
    },
    nextSend: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      display: "flex",
      alignItems: "center",
      gap: 5,
      "& .MuiSvgIcon-root": { fontSize: 14 },
    },
    footerActions: { marginLeft: "auto", display: "flex", gap: 10 },
    cancelButton: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.16)"}`,
      color: theme.palette.text.primary,
      borderRadius: 10,
      padding: "7px 16px",
      fontWeight: 600,
      fontSize: 13,
    },
    submitButton: {
      borderRadius: 10,
      padding: "7px 18px",
      fontWeight: 700,
      fontSize: 13,
      position: "relative",
      "& .MuiButton-startIcon": { marginRight: 4 },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    buttonProgress: {
      color: AEROS.success,
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
    contactOption: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13,
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
  };
});

const ScheduleSchema = Yup.object().shape({
  body: Yup.string().min(5, "Mensagem muito curta").required("Obrigatório"),
  contactId: Yup.number().required("Obrigatório"),
  sendAt: Yup.string().required("Obrigatório"),
  reminderDate: Yup.string().nullable(),
});

// Duplicata em JS da regra de fracionamento do backend (é exibição, não
// cobrança — a contagem oficial é sempre feita no servidor no envio).
const calcularFracoesSms = (texto) => {
  const len = (texto || "").length;
  if (len === 0) return 0;
  return len <= 160 ? 1 : Math.ceil(len / 153);
};

// Variáveis de destaque abaixo da mensagem (mockup) + lista completa no
// botão "Variáveis". Os tokens são os reais do backend ({{name}}, {{ms}}...).
const QUICK_VARS = [
  { value: "{{name}}", primary: true },
  { value: "{{ms}}" },
  { value: "{{protocol}}" },
];
const ALL_VARS = [
  "{{firstName}}",
  "{{name}}",
  "{{userName}}",
  "{{ms}}",
  "{{protocol}}",
  "{{date}}",
  "{{hour}}",
  "{{ticket_id}}",
  "{{queue}}",
  "{{connection}}",
  "{{dataAgendamento}}",
];

const ScheduleModal = ({
  open,
  onClose,
  scheduleId,
  contactId,
  cleanContact,
  reload,
  message,
  fromMessageInput = false,
  user,
}) => {
  const classes = useStyles();
  const aeros = useAerosSettingsStyles();
  const history = useHistory();
  const isMounted = useRef(true);
  const { user: authUser } = useContext(AuthContext);
  const currentUser = user || authUser;
  const isAdmin = currentUser.profile === "admin";

  const initialState = {
    body: message || "",
    contactId: contactId || "",
    sendAt: moment().add(1, "hour").format("YYYY-MM-DDTHH:mm"),
    sentAt: "",
    openTicket: "enabled",
    ticketUserId: currentUser.id,
    queueId: "",
    statusTicket: "open",
    intervalo: 1,
    valorIntervalo: 0,
    enviarQuantasVezes: 1,
    tipoDias: 4,
    assinar: false,
    reminderDate: "",
    canal: "whatsapp",
  };

  const initialContact = { id: "", name: "", channel: "" };

  const [schedule, setSchedule] = useState(initialState);
  const [currentContact, setCurrentContact] = useState(initialContact);
  const [contacts, setContacts] = useState([initialContact]);
  const [intervalo, setIntervalo] = useState(1);
  const [tipoDias, setTipoDias] = useState(4);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const messageInputRef = useRef();
  const [channelFilter, setChannelFilter] = useState("whatsapp");
  const [canal, setCanal] = useState("whatsapp");
  const ehSms = canal === "sms";
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState("");
  // Template aprovado do canal oficial. O agendamento dispara quase sempre
  // fora da janela de 24h da Meta, onde texto livre não passa — ver
  // scheduleTemplateRules.js.
  const [templates, setTemplates] = useState([]);
  const [totalAprovados, setTotalAprovados] = useState(0);
  const [templateName, setTemplateName] = useState(null);
  const [templateParams, setTemplateParams] = useState([]);
  // Guarda a conexão do render anterior para separar "usuário trocou de
  // conexão" (limpa o template) de "primeira definição / hidratação".
  const whatsappIdAnteriorRef = useRef(null);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const { findAll: findAllQueues } = useQueues();

  // Respostas rápidas
  const [quickMessages, setQuickMessages] = useState([]);
  const [loadingQuickMessages, setLoadingQuickMessages] = useState(false);
  const [quickMessageSearch, setQuickMessageSearch] = useState("");
  const [showQuickMessages, setShowQuickMessages] = useState(false);
  const [quickMessageMedia, setQuickMessageMedia] = useState(null);
  const [showAllVars, setShowAllVars] = useState(false);
  const [showReminder, setShowReminder] = useState(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);
      };
      loadQueues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open && currentUser?.companyId) {
      fetchQuickMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentUser?.companyId, currentUser?.id]);

  // Atendentes para o bloco "Abrir ticket ao enviar" (só admin escolhe).
  useEffect(() => {
    if (!open || !isAdmin) return;
    api
      .get("/users/list", { params: { companyId: currentUser.companyId } })
      .then(({ data }) => setUsers(isArray(data) ? data : []))
      .catch((err) => toastError(err));
  }, [open, isAdmin, currentUser.companyId]);

  const filteredQuickMessages = useMemo(() => {
    const term = quickMessageSearch.trim().toLowerCase();
    if (!term) return quickMessages;
    return quickMessages.filter(
      (qm) =>
        (qm.message || "").toLowerCase().includes(term) ||
        (qm.shortcode || "").toLowerCase().includes(term)
    );
  }, [quickMessageSearch, quickMessages]);

  const fetchQuickMessages = async () => {
    setLoadingQuickMessages(true);
    try {
      const { data } = await api.get("/quick-messages/list", {
        params: {
          companyId: currentUser?.companyId,
          userId: currentUser?.id,
          isOficial: "false",
        },
      });
      setQuickMessages(data || []);
    } catch (err) {
      toastError(err);
      setQuickMessages([]);
    } finally {
      setLoadingQuickMessages(false);
    }
  };

  const downloadQuickMessageMedia = async (mediaPath, mediaName, mediaType) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";
      const downloadUrl = `${backendUrl}/public/company${currentUser?.companyId}/quickMessage/${mediaName}`;

      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!response.ok) {
        throw new Error(`Erro ao baixar mídia: ${response.status}`);
      }

      const blob = await response.blob();
      return new File([blob], mediaName, {
        type: blob.type || getMediaTypeFromExtension(mediaName, mediaType),
      });
    } catch (err) {
      toastError(err);
      return null;
    }
  };

  const getMediaTypeFromExtension = (fileName, mediaType) => {
    const extension = fileName.split(".").pop().toLowerCase();
    switch (mediaType) {
      case "image":
        return `image/${extension === "jpg" ? "jpeg" : extension}`;
      case "audio":
        return `audio/${extension}`;
      case "video":
        return `video/${extension}`;
      default:
        return "application/octet-stream";
    }
  };

  useEffect(() => {
    api
      .get(`/whatsapp/filter`, {
        params: { session: 0, channel: channelFilter },
      })
      .then(({ data }) => {
        setWhatsapps(data);
        if (data.length === 1) {
          setSelectedWhatsapps(data[0].id);
        }
      })
      .catch(() => {});
  }, [currentContact, channelFilter]);

  const ehOficial = conexaoEhOficial(whatsapps, selectedWhatsapps);
  const mostraTemplate = exigeTemplate({ canal, ehOficial });
  const templateSelecionado =
    templates.find((t) => t.shortcode === templateName) || null;
  const tokensTemplate = tokensDoTemplate(templateSelecionado);
  const templateMisturaFormatos = templateSelecionado?.tipoVariaveis === "misto";

  // A lista vem do MESMO endpoint da campanha, que já filtra os elegíveis e
  // devolve os tokens do BODY na ordem em que a Meta espera os parâmetros.
  useEffect(() => {
    if (!mostraTemplate || !selectedWhatsapps) {
      setTemplates([]);
      setTotalAprovados(0);
      return;
    }

    api
      .get(`/whatsapp/${selectedWhatsapps}/templates`)
      .then(({ data }) => {
        setTemplates(data?.templates || []);
        setTotalAprovados(data?.totalAprovados || 0);
      })
      .catch(() => {
        setTemplates([]);
        setTotalAprovados(0);
      });
  }, [selectedWhatsapps, mostraTemplate]);

  useEffect(() => {
    const anterior = whatsappIdAnteriorRef.current;
    whatsappIdAnteriorRef.current = selectedWhatsapps || null;

    // Template pertence à conexão: mantê-lo depois da troca faria o backend
    // recusar o salvamento com "não está aprovado nesta conexão".
    if (deveLimparTemplateNaTrocaDeConexao(anterior, selectedWhatsapps || null)) {
      setTemplateName(null);
      setTemplateParams([]);
    }
  }, [selectedWhatsapps]);

  useEffect(() => {
    if (contactId && contacts.length) {
      const contact = contacts.find((c) => c.id === contactId);
      if (contact) {
        setCurrentContact(contact);
      }
    }
  }, [contactId, contacts]);

  useEffect(() => {
    const { companyId } = currentUser;
    if (open) {
      try {
        (async () => {
          const { data: contactList } = await api.get("/contacts/list", {
            params: { companyId },
          });

          const customList = contactList.map((c) => ({
            id: c.id,
            name: c.name,
            channel: c.channel,
          }));

          if (isArray(customList)) {
            setContacts([{ id: "", name: "", channel: "" }, ...customList]);
          }

          if (!scheduleId) {
            setSchedule({
              ...initialState,
              body: message || "",
              contactId: contactId || "",
            });
            setCanal("whatsapp");
            setShowReminder(false);

            if (contactId && customList.length > 0) {
              const foundContact = customList.find(
                (c) => c.id.toString() === contactId.toString()
              );
              if (foundContact) {
                setCurrentContact(foundContact);
                setChannelFilter(foundContact.channel || "whatsapp");
              }
            }
            return;
          }

          const { data } = await api.get(`/schedules/${scheduleId}`);
          setSchedule((prevState) => ({
            ...prevState,
            ...data,
            sendAt: moment(data.sendAt).format("YYYY-MM-DDTHH:mm"),
            reminderDate: data.reminderDate
              ? moment(data.reminderDate).format("YYYY-MM-DDTHH:mm")
              : "",
          }));
          setShowReminder(Boolean(data.reminderDate));
          setCanal(data.canal === "sms" ? "sms" : "whatsapp");
          setTemplateName(data.templateName || null);
          setTemplateParams(parseTemplateParams(data.templateParams));

          if (data.whatsapp) {
            setSelectedWhatsapps(data.whatsapp.id);
          }
          if (data.ticketUser) {
            setSelectedUser(data.ticketUser);
          }
          if (data.queueId) {
            setSelectedQueue(data.queueId);
          }
          if (data.intervalo) {
            setIntervalo(data.intervalo);
          }
          if (data.tipoDias) {
            setTipoDias(data.tipoDias);
          }
          setCurrentContact(data.contact);
        })();
      } catch (err) {
        toastError(err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, contactId, open, user, message, fromMessageInput]);

  const handleClose = () => {
    onClose();
    setAttachment(null);
    setSchedule(initialState);
    setCurrentContact(initialContact);
    setQuickMessageSearch("");
    setShowQuickMessages(false);
    setQuickMessageMedia(null);
    setCanal("whatsapp");
    setShowAllVars(false);
    setShowReminder(false);
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c" }} />;
      default:
        return <WhatsApp style={{ color: "#25d366" }} />;
    }
  };

  const handleSaveSchedule = async (values) => {
    // Variável em branco vira parâmetro vazio no envio: a Meta entrega o
    // template com um buraco e não há como corrigir depois do disparo.
    if (mostraTemplate && templateIncompleto(templateSelecionado, templateParams)) {
      toast.error(i18n.t("scheduleModal.template.incomplete"));
      return;
    }

    // Admin sem atendente escolhido cai no próprio usuário (era null antes).
    const ticketUserId = isAdmin ? selectedUser?.id || currentUser.id : currentUser.id;
    const scheduleData = {
      ...values,
      ...payloadTemplate({
        ehOficial: mostraTemplate,
        template: templateSelecionado,
        templateParams,
      }),
      userId: currentUser.id,
      canal,
      whatsappId: ehSms ? null : selectedWhatsapps || null,
      ticketUserId: ehSms ? null : ticketUserId,
      queueId: ehSms ? null : selectedQueue || null,
      intervalo: intervalo || 1,
      tipoDias: tipoDias || 4,
      reminderDate: values.reminderDate || null,
    };

    try {
      if (scheduleId) {
        await api.put(`/schedules/${scheduleId}`, scheduleData);
        if (attachment != null && !ehSms) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/schedules/${scheduleId}/media-upload`, formData);
        }
      } else {
        const { data } = await api.post("/schedules", scheduleData);
        if (attachment != null && !ehSms) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/schedules/${data.id}/media-upload`, formData);
        }
      }

      toast.success(i18n.t("scheduleModal.success"));

      if (typeof reload == "function") {
        reload();
      }

      if (contactId) {
        if (typeof cleanContact === "function") {
          cleanContact();
          history.push("/schedules");
        }
      }
    } catch (err) {
      toastError(err);
    }

    setCurrentContact(initialContact);
    setSchedule(initialState);
    handleClose();
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    if (!el) return;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;

    setValueFunc("body", `${firstHalfText}${msgVar}${secondHalfText}`);

    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
    messageInputRef.current.focus();
  };

  const handleQuickMessageSelect = async (selectedMessage, setFieldValue) => {
    if (!selectedMessage) return;
    setFieldValue("body", selectedMessage.message || "");
    setShowQuickMessages(false);
    setQuickMessageSearch("");

    if (selectedMessage.mediaPath) {
      const mediaFile = await downloadQuickMessageMedia(
        selectedMessage.mediaPath,
        selectedMessage.mediaName,
        selectedMessage.mediaType
      );
      if (mediaFile) {
        setAttachment(mediaFile);
        setQuickMessageMedia({
          path: selectedMessage.mediaPath,
          name: selectedMessage.mediaName,
          type: selectedMessage.mediaType,
        });
      }
    } else {
      setQuickMessageMedia(null);
      setAttachment(null);
      if (attachmentFile.current) {
        attachmentFile.current.value = null;
      }
    }
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      if (attachmentFile.current) attachmentFile.current.value = null;
    }
    if (quickMessageMedia) {
      setQuickMessageMedia(null);
    }
    if (schedule.mediaPath) {
      await api.delete(`/schedules/${schedule.id}/media-upload`);
      setSchedule((prev) => ({ ...prev, mediaPath: null }));
      toast.success(i18n.t("scheduleModal.toasts.deleted"));
      if (typeof reload == "function") {
        reload();
      }
    }
    setConfirmationOpen(false);
  };

  const selectedWhatsappName = useMemo(() => {
    const w = whatsapps.find((x) => x.id === selectedWhatsapps);
    return w ? w.name : "";
  }, [whatsapps, selectedWhatsapps]);

  const ticketUserName = (isAdmin && selectedUser?.name) || currentUser.name;
  const ticketQueueName = useMemo(() => {
    const q = queues.find((x) => x.id === selectedQueue);
    return q ? q.name : "";
  }, [queues, selectedQueue]);

  const attachmentName = attachment
    ? attachment.name
    : quickMessageMedia
    ? quickMessageMedia.name
    : schedule.mediaName;
  const hasAttachment = Boolean(attachment || quickMessageMedia || schedule.mediaPath);
  const isSent = Boolean(schedule.sentAt);

  return (
    <>
      <ConfirmationModal
        title={i18n.t("scheduleModal.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("scheduleModal.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        classes={{ paper: classes.paper }}
        scroll="paper"
      >
        <div style={{ display: "none" }}>
          <input
            type="file"
            accept=".png,.jpg,.jpeg"
            ref={attachmentFile}
            onChange={handleAttachmentFile}
          />
        </div>
        <Formik
          initialValues={schedule}
          enableReinitialize={true}
          validationSchema={ScheduleSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveSchedule(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => {
            const recorrente =
              Number(values.enviarQuantasVezes) > 1 || Number(values.valorIntervalo) > 0;
            const ticketOn = values.openTicket !== "disabled";
            const nextDates = nextSendDates({
              sendAt: values.sendAt,
              reminderDate: values.reminderDate,
              intervalo,
              valorIntervalo: values.valorIntervalo,
              enviarQuantasVezes: values.enviarQuantasVezes,
            });

            const toggleRecorrencia = (on) => {
              if (on) {
                setFieldValue("valorIntervalo", 1);
                setFieldValue("enviarQuantasVezes", 2);
                setIntervalo(3);
              } else {
                setFieldValue("valorIntervalo", 0);
                setFieldValue("enviarQuantasVezes", 1);
              }
            };

            return (
              <Form style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                <div className={classes.header}>
                  <div>
                    <div className={classes.title}>
                      {schedule.status === "ERRO"
                        ? i18n.t("scheduleModal.title.error")
                        : scheduleId
                        ? i18n.t("scheduleModal.title.edit")
                        : i18n.t("scheduleModal.title.add")}
                    </div>
                    <div className={classes.subtitle}>
                      {isSent
                        ? i18n.t("scheduleModal.subtitle.sent", {
                            date: moment(schedule.sentAt).format("DD/MM/YYYY HH:mm"),
                          })
                        : i18n.t("scheduleModal.subtitle.default")}
                    </div>
                  </div>
                  <IconButton
                    className={classes.closeButton}
                    onClick={handleClose}
                    aria-label={i18n.t("scheduleModal.buttons.cancel")}
                  >
                    <CloseIcon />
                  </IconButton>
                </div>

                <div className={classes.body}>
                  {/* Motivo da falha: o texto do backend diz a ação que
                      resolve (ex.: criar template aprovado). Antes ele só
                      existia no log, e a tela mostrava um chip vermelho sem
                      explicação nenhuma. */}
                  {(schedule.status === "ERRO" ||
                    schedule.reminderStatus === "ERRO") && (
                    <div
                      className={`${classes.templateAlert} ${classes.templateAlertError}`}
                      role="alert"
                    >
                      <b>{i18n.t("scheduleModal.failure.title")}</b>
                      <div>
                        <MotivoFalha
                          reason={schedule.failureReason}
                          fallback={i18n.t("scheduleModal.failure.unknown")}
                        />
                      </div>
                    </div>
                  )}

                  {/* Contato · Data de envio · Canal */}
                  <div className={classes.grid3}>
                    <div>
                      <label className={classes.fieldLabel}>
                        {i18n.t("scheduleModal.form.contact")}
                      </label>
                      <Autocomplete
                        fullWidth
                        value={currentContact}
                        options={contacts}
                        onChange={(e, contact) => {
                          const newContactId = contact ? contact.id : "";
                          setFieldValue("contactId", newContactId);
                          setSchedule({ ...schedule, contactId: newContactId });
                          setCurrentContact(contact ? contact : initialContact);
                          setChannelFilter(contact ? contact.channel : "whatsapp");
                        }}
                        getOptionLabel={(option) => option.name || ""}
                        renderOption={(option) => (
                          <span className={classes.contactOption}>
                            {IconChannel(option.channel)}
                            {option.name}
                          </span>
                        )}
                        getOptionSelected={(option, value) => value.id === option.id}
                        popupIcon={<ExpandMoreIcon />}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            className={classes.input}
                            placeholder={i18n.t("scheduleModal.form.contactPlaceholder")}
                            error={touched.contactId && Boolean(errors.contactId)}
                            helperText={touched.contactId && errors.contactId}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: currentContact?.id ? (
                                <InputAdornment position="start">
                                  {IconChannel(currentContact.channel)}
                                </InputAdornment>
                              ) : null,
                            }}
                          />
                        )}
                      />
                    </div>
                    <div>
                      <label className={classes.fieldLabel} htmlFor="schedule-sendAt">
                        {i18n.t("scheduleModal.form.sendAt")}
                      </label>
                      <Field
                        as={TextField}
                        id="schedule-sendAt"
                        type="datetime-local"
                        name="sendAt"
                        variant="outlined"
                        className={`${classes.input} ${classes.dateInput}`}
                        error={touched.sendAt && Boolean(errors.sendAt)}
                        helperText={touched.sendAt && errors.sendAt}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              onClick={() => {
                                const el = document.getElementById("schedule-sendAt");
                                if (el && el.showPicker) el.showPicker();
                              }}
                            >
                              <EventOutlinedIcon className={classes.dateIcon} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                    <div>
                      <label className={classes.fieldLabel}>
                        {i18n.t("scheduleModal.form.canal")}
                      </label>
                      <div className={classes.segmented} role="tablist">
                        {[
                          ["whatsapp", "WhatsApp"],
                          ["sms", "SMS"],
                        ].map(([value, label]) => (
                          <button
                            key={value}
                            type="button"
                            role="tab"
                            aria-selected={canal === value}
                            className={`${classes.segment} ${
                              canal === value ? classes.segmentActive : ""
                            }`}
                            onClick={() => {
                              setFieldValue("canal", value);
                              setCanal(value);
                              // Anexo é exclusivo do WhatsApp: ao trocar para
                              // SMS, descarta qualquer arquivo pendente.
                              if (value === "sms" && attachment) {
                                setAttachment(null);
                                if (attachmentFile.current) {
                                  attachmentFile.current.value = null;
                                }
                              }
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Mensagem */}
                  <div>
                    <div className={classes.messageHead}>
                      <label className={classes.fieldLabel} style={{ marginBottom: 0 }}>
                        {ehSms
                          ? i18n.t("scheduleModal.form.bodySms")
                          : i18n.t("scheduleModal.form.body")}
                      </label>
                      <div className={classes.messageTools}>
                        <button
                          type="button"
                          className={`${classes.toolChip} ${classes.toolChipPurple}`}
                          onClick={() => setShowQuickMessages((v) => !v)}
                          disabled={loadingQuickMessages}
                        >
                          <FlashOnIcon /> {i18n.t("scheduleModal.tools.quickMessages")}
                        </button>
                        <button
                          type="button"
                          className={classes.toolChip}
                          onClick={() => setShowAllVars((v) => !v)}
                        >
                          <CodeIcon /> {i18n.t("scheduleModal.tools.variables")}
                        </button>
                        {!ehSms && (
                          <button
                            type="button"
                            className={classes.toolChip}
                            onClick={() => attachmentFile.current.click()}
                            disabled={isSubmitting}
                          >
                            <AttachFileIcon /> {i18n.t("scheduleModal.tools.attach")}
                          </button>
                        )}
                      </div>
                    </div>
                    <Field
                      as={TextField}
                      name="body"
                      multiline
                      minRows={3}
                      variant="outlined"
                      className={`${classes.input} ${classes.messageInput}`}
                      inputRef={messageInputRef}
                      error={touched.body && Boolean(errors.body)}
                      helperText={touched.body && errors.body}
                      placeholder={i18n.t("scheduleModal.form.bodyPlaceholder")}
                    />
                    {showQuickMessages && (
                      <div className={classes.quickList}>
                        <div className={classes.quickSearch}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            autoFocus
                            className={`${classes.input} ${classes.inputSmall}`}
                            placeholder={i18n.t("scheduleModal.tools.quickMessagesSearch")}
                            value={quickMessageSearch}
                            onChange={(e) => setQuickMessageSearch(e.target.value)}
                          />
                        </div>
                        {filteredQuickMessages.length === 0 && (
                          <div className={classes.quickEmpty}>
                            {i18n.t("scheduleModal.tools.quickMessagesEmpty")}
                          </div>
                        )}
                        {filteredQuickMessages.map((quickMessage) => (
                          <div
                            key={quickMessage.id}
                            className={classes.quickItem}
                            onClick={() => handleQuickMessageSelect(quickMessage, setFieldValue)}
                          >
                            <FlashOnIcon />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className={classes.quickShortcode}>{quickMessage.shortcode}</div>
                              <div className={classes.quickMessage}>{quickMessage.message}</div>
                            </div>
                            {quickMessage.mediaPath && <AttachFileIcon />}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className={classes.varRow}>
                      {(showAllVars ? ALL_VARS.map((value) => ({ value })) : QUICK_VARS).map(
                        (v) => (
                          <button
                            key={v.value}
                            type="button"
                            className={`${classes.varChip} ${
                              v.primary ? classes.varChipPrimary : ""
                            }`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              if (!isSubmitting) handleClickMsgVar(v.value, setFieldValue);
                            }}
                          >
                            {v.value}
                          </button>
                        )
                      )}
                    </div>
                    {ehSms && (
                      <div className={classes.helperText}>
                        {i18n.t("scheduleModal.form.smsCount", {
                          chars: (values.body || "").length,
                          parts: calcularFracoesSms(values.body),
                        })}
                      </div>
                    )}
                    {ehSms && schedule.mediaPath && (
                      <div className={`${classes.helperText} ${classes.helperError}`}>
                        {i18n.t("scheduleModal.form.smsAttachmentWarning")}
                      </div>
                    )}
                    {hasAttachment && (!ehSms || schedule.mediaPath) && (
                      <div className={classes.attachmentRow}>
                        <AttachFileIcon />
                        <span className={classes.attachmentName}>{attachmentName}</span>
                        {quickMessageMedia && (
                          <span className={aeros.chipPurple}>
                            {i18n.t("scheduleModal.form.fromQuickMessage")}
                          </span>
                        )}
                        <IconButton
                          size="small"
                          className={classes.attachmentRemove}
                          onClick={() => setConfirmationOpen(true)}
                          aria-label={i18n.t("scheduleModal.buttons.removeAttachment")}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </div>
                    )}
                  </div>

                  {/* Conexão · Abrir ticket ao enviar */}
                  {!ehSms && (
                    <div className={classes.grid2}>
                      <div>
                        <label className={classes.fieldLabel} htmlFor="schedule-whatsapp">
                          {i18n.t("scheduleModal.form.connection")}
                        </label>
                        <TextField
                          id="schedule-whatsapp"
                          select
                          variant="outlined"
                          className={classes.input}
                          value={selectedWhatsapps || ""}
                          onChange={(e) => setSelectedWhatsapps(e.target.value)}
                          SelectProps={{
                            IconComponent: ExpandMoreIcon,
                            displayEmpty: true,
                            renderValue: (v) =>
                              v ? selectedWhatsappName : (
                                <span style={{ opacity: 0.6 }}>
                                  {i18n.t("scheduleModal.form.connectionPlaceholder")}
                                </span>
                              ),
                          }}
                        >
                          {whatsapps.map((whatsapp) => (
                            <MenuItem key={whatsapp.id} value={whatsapp.id}>
                              {whatsapp.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </div>
                      <div>
                        <label className={classes.fieldLabel}>
                          {i18n.t("scheduleModal.form.openTicket")}
                        </label>
                        <div className={classes.toggleRow}>
                          <Switch
                            className={aeros.toggleSwitch}
                            checked={ticketOn}
                            onChange={(e) =>
                              setFieldValue("openTicket", e.target.checked ? "enabled" : "disabled")
                            }
                            color="primary"
                            inputProps={{ "aria-label": i18n.t("scheduleModal.form.openTicket") }}
                          />
                          <span className={classes.toggleText}>
                            {ticketOn ? (
                              <>
                                {i18n.t("scheduleModal.form.ticketFor")}{" "}
                                <b className={classes.toggleStrong}>
                                  {ticketUserName || "—"}
                                </b>{" "}
                                · {i18n.t("scheduleModal.form.ticketQueue")}{" "}
                                <b className={classes.toggleStrong}>{ticketQueueName || "—"}</b>
                              </>
                            ) : (
                              i18n.t("scheduleModal.form.ticketOff")
                            )}
                          </span>
                        </div>
                      </div>
                      {ticketOn && (
                        <div className={`${classes.ticketDetails} ${classes.ticketDetailsFull}`}>
                          <div>
                            <label className={classes.fieldLabel}>
                              {i18n.t("scheduleModal.form.ticketUser")}
                            </label>
                            <Autocomplete
                              options={isAdmin ? users : [currentUser]}
                              value={isAdmin ? selectedUser : currentUser}
                              disabled={!isAdmin}
                              getOptionLabel={(option) => option?.name || ""}
                              getOptionSelected={(option, value) => option?.id === value?.id}
                              popupIcon={<ExpandMoreIcon />}
                              onChange={(e, newValue) => {
                                setSelectedUser(newValue);
                                if (newValue && isArray(newValue.queues) && newValue.queues.length) {
                                  setQueues(newValue.queues);
                                  if (newValue.queues.length === 1) {
                                    setSelectedQueue(newValue.queues[0].id);
                                  }
                                } else {
                                  setQueues(allQueues);
                                  setSelectedQueue("");
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  className={`${classes.input} ${classes.inputSmall}`}
                                  placeholder={i18n.t("scheduleModal.form.ticketUserPlaceholder")}
                                />
                              )}
                            />
                          </div>
                          <div>
                            <label className={classes.fieldLabel}>
                              {i18n.t("scheduleModal.form.ticketQueueLabel")}
                            </label>
                            <TextField
                              select
                              variant="outlined"
                              className={`${classes.input} ${classes.inputSmall}`}
                              value={selectedQueue || ""}
                              onChange={(e) => setSelectedQueue(e.target.value)}
                              SelectProps={{ IconComponent: ExpandMoreIcon, displayEmpty: true }}
                            >
                              <MenuItem value="">
                                <em>{i18n.t("scheduleModal.form.ticketQueuePlaceholder")}</em>
                              </MenuItem>
                              {queues.map((queue) => (
                                <MenuItem key={queue.id} value={queue.id}>
                                  {queue.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </div>
                          <div>
                            <label className={classes.fieldLabel}>
                              {i18n.t("scheduleModal.form.statusTicket")}
                            </label>
                            <Field
                              as={TextField}
                              select
                              name="statusTicket"
                              variant="outlined"
                              className={`${classes.input} ${classes.inputSmall}`}
                              SelectProps={{ IconComponent: ExpandMoreIcon }}
                            >
                              <MenuItem value="open">
                                {i18n.t("campaigns.dialog.form.openTicketStatus")}
                              </MenuItem>
                              <MenuItem value="closed">
                                {i18n.t("campaigns.dialog.form.closedTicketStatus")}
                              </MenuItem>
                            </Field>
                          </div>
                          <div className={classes.assinarRow} style={{ alignSelf: "end", paddingBottom: 8 }}>
                            <Switch
                              className={aeros.toggleSwitchSmall}
                              checked={Boolean(values.assinar)}
                              onChange={(e) => setFieldValue("assinar", e.target.checked)}
                              color="primary"
                            />
                            {i18n.t("scheduleModal.form.assinar")}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Template aprovado (WhatsApp Oficial) */}
                  {mostraTemplate && (
                    <div className={classes.section}>
                      <div className={classes.sectionHead}>
                        <DescriptionOutlinedIcon />
                        <div style={{ flex: 1 }}>
                          <div className={classes.sectionTitle}>
                            {i18n.t("scheduleModal.template.title")}
                          </div>
                          <div className={classes.sectionDesc}>
                            {i18n.t("scheduleModal.template.subtitle")}
                          </div>
                        </div>
                      </div>
                      <div className={classes.templateBody}>
                        <TextField
                          select
                          variant="outlined"
                          className={`${classes.input} ${classes.inputSmall}`}
                          value={templateName || ""}
                          disabled={isSubmitting || templates.length === 0}
                          onChange={(e) => {
                            setTemplateName(e.target.value || null);
                            setTemplateParams([]);
                          }}
                          SelectProps={{
                            IconComponent: ExpandMoreIcon,
                            displayEmpty: true,
                            renderValue: (v) =>
                              v || (
                                <span style={{ opacity: 0.6 }}>
                                  {i18n.t("scheduleModal.template.placeholder")}
                                </span>
                              ),
                          }}
                          inputProps={{
                            "aria-label": i18n.t("scheduleModal.template.select"),
                          }}
                        >
                          <MenuItem value="">
                            <em>{i18n.t("scheduleModal.template.none")}</em>
                          </MenuItem>
                          {templates.map((template) => (
                            <MenuItem key={template.id} value={template.shortcode}>
                              {template.shortcode} ({template.language})
                            </MenuItem>
                          ))}
                        </TextField>

                        {templateMisturaFormatos && (
                          <div
                            className={`${classes.templateAlert} ${classes.templateAlertError}`}
                          >
                            {i18n.t("scheduleModal.template.mixed")}
                          </div>
                        )}

                        {templateSelecionado && tokensTemplate.length > 0 && (
                          <div className={classes.templateGrid}>
                            {tokensTemplate.map((token, indice) => (
                              <TextField
                                key={token}
                                variant="outlined"
                                className={`${classes.input} ${classes.inputSmall}`}
                                label={i18n.t("scheduleModal.template.paramLabel", {
                                  token: `{{${token}}}`,
                                })}
                                helperText={i18n.t("scheduleModal.template.paramHelper")}
                                value={templateParams[indice] || ""}
                                disabled={isSubmitting || templateMisturaFormatos}
                                onChange={(e) => {
                                  const valores = [...templateParams];
                                  valores[indice] = e.target.value;
                                  setTemplateParams(valores);
                                }}
                              />
                            ))}
                          </div>
                        )}

                        {templateSelecionado && (
                          <div>
                            <div className={classes.templatePreviewLabel}>
                              {i18n.t("scheduleModal.template.preview")}
                            </div>
                            <div className={classes.templatePreview}>
                              {previa(templateSelecionado, templateParams) ||
                                textoDoBody(templateSelecionado.components)}
                            </div>
                          </div>
                        )}

                        {(() => {
                          const aviso = avisoTemplate({
                            templates,
                            totalAprovados,
                            template: templateSelecionado,
                          });

                          if (!aviso) return null;

                          return (
                            <div
                              className={`${classes.templateAlert} ${
                                aviso.severity === "warning"
                                  ? classes.templateAlertWarning
                                  : classes.templateAlertInfo
                              }`}
                            >
                              {aviso.text}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Recorrência */}
                  <div className={classes.section}>
                    <div className={classes.sectionHead}>
                      <RepeatIcon />
                      <div style={{ flex: 1 }}>
                        <div className={classes.sectionTitle}>
                          {i18n.t("recurrenceSection.title")}
                        </div>
                        <div className={classes.sectionDesc}>
                          {i18n.t("scheduleModal.recurrence.subtitle")}
                        </div>
                      </div>
                      <Switch
                        className={aeros.toggleSwitch}
                        checked={recorrente}
                        onChange={(e) => toggleRecorrencia(e.target.checked)}
                        color="primary"
                        inputProps={{ "aria-label": i18n.t("recurrenceSection.title") }}
                      />
                    </div>
                    {recorrente && (
                      <div className={classes.recurrenceGrid}>
                        <div className={classes.everyCell}>
                          <span className={classes.everyPrefix}>
                            {i18n.t("scheduleModal.recurrence.every")}
                          </span>
                          <Field
                            as={TextField}
                            name="valorIntervalo"
                            type="number"
                            inputProps={{ min: 1 }}
                            variant="outlined"
                            className={`${classes.input} ${classes.inputSmall} ${classes.everyNumber}`}
                            error={touched.valorIntervalo && Boolean(errors.valorIntervalo)}
                          />
                          <TextField
                            select
                            variant="outlined"
                            className={`${classes.input} ${classes.inputSmall} ${classes.everyUnit}`}
                            value={intervalo}
                            onChange={(e) => setIntervalo(e.target.value || 1)}
                            SelectProps={{ IconComponent: ExpandMoreIcon }}
                          >
                            <MenuItem value={1}>{i18n.t("recurrenceSection.options.days")}</MenuItem>
                            <MenuItem value={2}>{i18n.t("recurrenceSection.options.weeks")}</MenuItem>
                            <MenuItem value={3}>{i18n.t("recurrenceSection.options.months")}</MenuItem>
                            <MenuItem value={4}>{i18n.t("recurrenceSection.options.minutes")}</MenuItem>
                          </TextField>
                        </div>
                        <Field
                          as={TextField}
                          name="enviarQuantasVezes"
                          type="number"
                          inputProps={{ min: 1 }}
                          variant="outlined"
                          className={`${classes.input} ${classes.inputSmall}`}
                          error={touched.enviarQuantasVezes && Boolean(errors.enviarQuantasVezes)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <span style={{ fontSize: 12.5 }}>
                                  {i18n.t("scheduleModal.recurrence.repeat")}
                                </span>
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <span style={{ fontSize: 12.5 }}>
                                  {i18n.t("scheduleModal.recurrence.times")}
                                </span>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          select
                          variant="outlined"
                          className={`${classes.input} ${classes.inputSmall}`}
                          value={tipoDias}
                          onChange={(e) => setTipoDias(e.target.value || 4)}
                          SelectProps={{ IconComponent: ExpandMoreIcon }}
                        >
                          <MenuItem value={4}>
                            {i18n.t("scheduleModal.recurrence.weekendNormal")}
                          </MenuItem>
                          <MenuItem value={5}>
                            {i18n.t("scheduleModal.recurrence.weekendBefore")}
                          </MenuItem>
                          <MenuItem value={6}>
                            {i18n.t("scheduleModal.recurrence.weekendAfter")}
                          </MenuItem>
                        </TextField>
                      </div>
                    )}
                  </div>

                  {/* Lembrete */}
                  <div className={classes.reminder}>
                    <NotificationsNoneIcon />
                    <div className={classes.reminderText}>
                      <b style={{ color: "inherit" }} className={classes.toggleStrong}>
                        {i18n.t("scheduleModal.reminder.title")}
                      </b>{" "}
                      — {i18n.t("scheduleModal.reminder.description")}
                    </div>
                    {!showReminder && !values.reminderDate && (
                      <button
                        type="button"
                        className={classes.reminderLink}
                        onClick={() => setShowReminder(true)}
                      >
                        + {i18n.t("scheduleModal.reminder.set")}
                      </button>
                    )}
                    {(showReminder || values.reminderDate) && (
                      <div className={classes.reminderField}>
                        <Field
                          as={TextField}
                          type="datetime-local"
                          name="reminderDate"
                          variant="outlined"
                          className={`${classes.input} ${classes.inputSmall}`}
                          style={{ maxWidth: 260 }}
                          error={touched.reminderDate && Boolean(errors.reminderDate)}
                          helperText={touched.reminderDate && errors.reminderDate}
                        />
                        <button
                          type="button"
                          className={classes.reminderLink}
                          onClick={() => {
                            setFieldValue("reminderDate", "");
                            setShowReminder(false);
                          }}
                        >
                          {i18n.t("scheduleModal.reminder.remove")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className={classes.footer}>
                  {nextDates.length > 0 && (
                    <div className={classes.nextSend}>
                      <EventRepeatIcon />
                      {i18n.t("scheduleModal.footer.nextSend")}: {nextDates[0].format("DD/MM HH:mm")}
                      {nextDates[1] &&
                        ` · ${i18n.t("scheduleModal.footer.then")} ${nextDates[1].format("DD/MM HH:mm")}`}
                    </div>
                  )}
                  <div className={classes.footerActions}>
                    <Button
                      onClick={handleClose}
                      disabled={isSubmitting}
                      variant="outlined"
                      className={classes.cancelButton}
                    >
                      {i18n.t("scheduleModal.buttons.cancel")}
                    </Button>
                    {!isSent && (
                      <Button
                        type="submit"
                        color="primary"
                        disabled={isSubmitting}
                        variant="contained"
                        className={classes.submitButton}
                        startIcon={<SendIcon />}
                      >
                        {scheduleId
                          ? i18n.t("scheduleModal.buttons.okEdit")
                          : i18n.t("scheduleModal.buttons.okAdd")}
                        {isSubmitting && (
                          <CircularProgress size={24} className={classes.buttonProgress} />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
};

export default ScheduleModal;
